import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useSite } from "../lib/site";
import { ErrorState, PageLoader } from "../components/States";
import MobileActionBar from "../components/MobileActionBar";
import { enquireHref } from "../lib/format";
import ArticleTemplate from "../templates/ArticleTemplate";
import ContactTemplate from "../templates/ContactTemplate";
import GalleryTemplate from "../templates/GalleryTemplate";
import HubTemplate from "../templates/HubTemplate";
import ListingTemplate from "../templates/ListingTemplate";
import ProductTemplate from "../templates/ProductTemplate";
import RangeTemplate from "../templates/RangeTemplate";

const TEMPLATES = {
  range: RangeTemplate,
  listing: ListingTemplate,
  hub: HubTemplate,
  gallery: GalleryTemplate,
  article: ArticleTemplate,
  contact: ContactTemplate,
};

/**
 * Content-led page types render through ProductTemplate: it puts a sticky quote
 * + gallery rail on the right so no page is left with an empty half. Hub pages
 * keep their full-width directory grid, listings keep their filter toolbar, and
 * the contact page keeps its form layout.
 */
const PRODUCT_LAYOUT = new Set(["range", "gallery", "article"]);

/**
 * Router for every migrated page.
 *
 * The source site pushed all 116 pages through one WordPress template. The API
 * classifies each migrated page by the shape of its content and this component
 * dispatches to a purpose-built layout, so a brand range, a category hub, a
 * photo gallery and a blog post no longer look identical.
 */
export default function DynamicPage() {
  const location = useLocation();
  const { site } = useSite();
  const slug = useMemo(
    () => decodeURIComponent(location.pathname.replace(/^\/+|\/+$/g, "")) || "home",
    [location.pathname]
  );

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setData(null);
    setError(null);
    api
      .get(`/pages/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "notfound" : "error"));
  }, [slug]);

  useEffect(() => {
    load();
    window.scrollTo({ top: 0 });
  }, [load]);

  useEffect(() => {
    if (data?.page) {
      document.title = data.page.seo_title || `${data.page.title} - NQ Springs`;
    }
  }, [data]);

  if (error === "notfound") {
    return (
      <div className="container-nq section-nq">
        <ErrorState
          title="Page not found"
          message={`We couldn't find "${slug}". It may have moved.`}
        />
        <div className="flex justify-center gap-3">
          <Link to="/" className="btn-gold" data-testid="notfound-home-link">
            Back home
          </Link>
          <Link to="/site-index" className="btn-outline-nq" data-testid="notfound-index-link">
            Browse all pages
          </Link>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="container-nq section-nq">
        <ErrorState message="We couldn't load this page." onRetry={load} />
      </div>
    );
  }
  if (!data) return <PageLoader />;

  const { page, items = [], children = [], siblings = [], breadcrumbs = [], template, structured } = data;
  const hasStructure = (structured?.variants || []).length > 0;
  const useProduct = hasStructure || PRODUCT_LAYOUT.has(template);
  const Template = useProduct ? ProductTemplate : TEMPLATES[template] || ArticleTemplate;
  const parentLabel =
    breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 2].label : "";

  return (
    <div
      data-testid="dynamic-page"
      data-page-slug={page.slug}
      data-page-template={useProduct ? "product" : template}
      data-page-kind={template}
      className={template === "contact" ? "" : "pb-24 lg:pb-0"}
    >
      <Template
        page={page}
        structured={structured}
        items={items}
        childPages={children}
        siblings={siblings}
        breadcrumbs={breadcrumbs}
        parentLabel={parentLabel}
        phone={site?.contact?.phone || ""}
        contact={site?.contact || {}}
      />
      {template !== "contact" && (
        <MobileActionBar
          to={enquireHref({ type: "page", id: page.slug, title: page.title, image: page.hero_image })}
          phone={site?.contact?.phone || ""}
        />
      )}
    </div>
  );
}
